import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import dayjs from "dayjs";
import { CashFlowInjection } from "features/cashFlow/di/CashFlowInjection";
import {
  ExportPayload,
  FetchListInput,
} from "features/cashFlow/domain/entities/CashFlowPayload";
import { CashFlowRequestEntity } from "features/cashFlow/domain/entities/CashFlowRequestEntity";
import { CashFlowResponseEntity } from "features/cashFlow/domain/entities/CashFlowResponseEntity";
import { CashFlowUseCase } from "features/cashFlow/domain/useCases/CashFlowUseCase";
import isEmpty from "lodash/isEmpty";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import { DateFormat } from "utils/format/DateFormat";
import { strings } from "utils/localizedStrings";
import { ICashFlowObservedState } from "../interfaces/ICashFlow";

export interface IDropdownActionRecord {
  record: CashFlowResponseEntity;
  action: string;
}

export type DetailSelectionType = CashFlowResponseEntity | IDropdownActionRecord;

type FilterOptionsState = Pick<
  ICashFlowObservedState,
  "storeOptions" | "staffOptions" | "userOptions"
>;

class CashFlowInput implements Disposable {
  public fetchList = new BehaviorSubject<FetchListInput>({
    kind: "params",
    params: new CashFlowRequestEntity(),
  });
  public exportExcel = new BehaviorSubject<ExportPayload | null>(null);
  public fetchDetail = new BehaviorSubject<CashFlowRequestEntity>(
    new CashFlowRequestEntity(),
  );
  public selectDetail = new BehaviorSubject<DetailSelectionType>({});
  public syncFilterOptions = new BehaviorSubject<Partial<FilterOptionsState>>(
    {},
  );
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.selectDetail.complete();
    this.syncFilterOptions.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
  }
}

class CashFlowOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onDetailLoaded = new BehaviorSubject<{ action?: string } | null>(null);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onDetailLoaded.complete();
  }
}

@injectable()
export class CashFlowViewModel extends BasedObservedViewModel<
  CashFlowInput,
  CashFlowOutput,
  ICashFlowObservedState
> {
  private cashFlowUseCase: CashFlowUseCase;

  protected override createObservedState(): ICashFlowObservedState {
    return {
      list: [],
      detail: {},
      pagination: { page: 0, total: 0, limit: 10 },
      loading: false,
      detailLoading: false,
      queryParams: new CashFlowRequestEntity({
        page: 0,
        limit: 10,
        ...DateFormat.getDateRange(0),
        serviceCode: ServiceEnum.CASH_FLOW,
      }),
      partnerOptions: [],
      storeOptions: [],
      staffOptions: [],
      userOptions: [{ label: strings.GlobalLabels.All, value: "" }],
    };
  }

  constructor() {
    super(new CashFlowInput(), new CashFlowOutput());
    this.cashFlowUseCase = CashFlowInjection.getUseCase();
  }

  private downloadExcel(data: Blob): void {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute("download", `CASH_FLOW_REPORT_${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private toPartnerOptions(items: BaseFilterListResponseModel[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: `${item.name}${
          item.providerCode ? ` - ${item.providerCode.toUpperCase()}` : ""
        }`,
        value: item.code,
        type: item.type,
        isStore: item.isStore,
        providerCode: item.providerCode,
      })),
    ];
  }

  private toStoreOptions(items: BaseFilterListResponseModel[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: item.store_name,
        value: item.store_code,
      })),
    ];
  }

  private toUserOptions(items: BaseUserResponseModel[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({ label: item.name, value: item.id })),
    ];
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (input, guard) => {
      try {
        guard?.runIfLatest(() => {
          const currentPaging = this.observedState.pagination;
          const pagination =
            input.kind === "tableChange"
              ? {
                  page: input.page,
                  limit: input.limit,
                  total: currentPaging.total,
                  deposit: currentPaging.deposit,
                  withdrawal: currentPaging.withdrawal,
                  debit: currentPaging.debit,
                  refund: currentPaging.refund,
                }
              : input.kind === "search" || input.kind === "redo"
                ? { page: 0, limit: currentPaging.limit, total: 0 }
                : currentPaging;
          this.setObservedState({
            loading: true,
            list: [],
            pagination,
          });
        });

        let res;
        let paramsToSave: CashFlowRequestEntity | null = null;

        switch (input.kind) {
          case "search": {
            const result = await this.cashFlowUseCase.getListFromSearch(
              input.rawFormValues,
              input.context,
            );
            res = result.list;
            paramsToSave = result.params;
            break;
          }
          case "tableChange": {
            const result = await this.cashFlowUseCase.getListWithTableChange(
              input.prevParams,
              input.rawFormValues,
              input.page,
              input.limit,
            );
            res = result.list;
            paramsToSave = result.params;
            break;
          }
          case "redo": {
            const result = await this.cashFlowUseCase.getListRedo();
            res = result.list;
            paramsToSave = result.params;
            break;
          }
          case "params":
          default:
            res = await this.cashFlowUseCase.getList(input.params);
            break;
        }

        guard?.runIfLatest(() => {
          if (res?.data) {
            this.setObservedState({
              list: res.data,
              pagination: res.pagination ?? {},
              ...(paramsToSave ? { queryParams: paramsToSave } : {}),
            });
          }
        });
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => {
          this.setObservedState({ list: [] });
          this.output.onError.next(error as Error);
        });
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params, guard) => {
      if (isEmpty(params)) return;
      try {
        guard?.runIfLatest(() =>
          this.setObservedState({ detailLoading: true }),
        );
        const res = await this.cashFlowUseCase.getDetail(params);
        if (res?.data) {
          guard?.runIfLatest(() => {
            this.setObservedState({ detail: res.data });
            this.output.onDetailLoaded.next({});
          });
        }
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.output.onError.next(error as Error));
      } finally {
        guard?.runIfLatest(() =>
          this.setObservedState({ detailLoading: false }),
        );
      }
    });

    this.skipFirstInitial(this.input.selectDetail, (selection) => {
      if ("action" in selection) {
        this.setObservedState({ detail: selection.record });
        this.output.onDetailLoaded.next({ action: selection.action });
        return;
      }
      this.setObservedState({ detail: selection });
      this.output.onDetailLoaded.next({});
    });

    this.skipFirstInitial(this.input.syncFilterOptions, (options) => {
      this.setObservedState(options);
    });

    this.skipFirstInitial(this.input.exportExcel, async (payload, guard) => {
      if (!payload) return;
      try {
        guard?.runIfLatest(() => this.setObservedState({ loading: true }));
        const res = await this.cashFlowUseCase.exportReportFromSearch(payload);
        const data = res?.data;
        if (data) {
          guard?.runIfLatest(() => {
            if (data.size < 1000) {
              this.output.onError.next({
                message: strings.GlobalMessage.ExcelError,
              });
              return;
            }
            this.downloadExcel(data);
            this.output.onExportSuccess.next(true);
          });
        }
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.output.onError.next(error as Error));
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params, guard) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);
        if (!isEmpty(cache)) {
          guard?.runIfLatest(() =>
            this.setObservedState({
              partnerOptions: this.toPartnerOptions(cache),
            }),
          );
          return;
        }
        const res = await this.cashFlowUseCase.getFilterList(params, "partner");
        const data = res ?? [];
        guard?.runIfLatest(() =>
          this.setObservedState({
            partnerOptions: this.toPartnerOptions(data),
          }),
        );
        productCache.saveCache({
          key: CacheKeyEnum.partnerList,
          product: data,
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        guard?.runIfLatest(() => {
          this.setObservedState({ partnerOptions: [] });
          this.output.onError.next(error as Error);
        });
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params, guard) => {
      const { partnerCode } = params;
      if (!partnerCode) return;
      const cacheKey = `${CacheKeyEnum.storeList}-${partnerCode}`;
      const cache = productCache.getCache(cacheKey);
      if (cache?.length) {
        guard?.runIfLatest(() =>
          this.setObservedState({ storeOptions: this.toStoreOptions(cache) }),
        );
        return;
      }
      try {
        const res = await this.cashFlowUseCase.getFilterList(params, "store");
        guard?.runIfLatest(() =>
          this.setObservedState({
            storeOptions: this.toStoreOptions(res ?? []),
          }),
        );
        productCache.saveCache({
          key: cacheKey,
          product: res,
        });
      } catch (error) {
        guard?.runIfLatest(() => this.output.onError.next(error as Error));
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params, guard) => {
      if (isEmpty(params.storeCode)) {
        guard?.runIfLatest(() => this.setObservedState({ staffOptions: [] }));
        return;
      }
      try {
        const res = await this.cashFlowUseCase.getUserForFilter(
          params,
          "staff",
        );
        const allUsers = (res ?? []).flatMap((item) => item?.users ?? []);
        const mappedUsers = allUsers.map((user) => ({
          id: user.id,
          name: user.name || user.userName || user.email || user.phone || "",
          code: user.code,
        }));
        guard?.runIfLatest(() =>
          this.setObservedState({
            staffOptions: this.toUserOptions(mappedUsers),
          }),
        );
      } catch (error) {
        guard?.runIfLatest(() => {
          this.setObservedState({ staffOptions: [] });
          this.output.onError.next(error as Error);
        });
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params, guard) => {
      try {
        const filterType =
          params.providerCode === "mobitopup" || params.providerCode === "smm"
            ? "staff"
            : "userB2B";
        const res = await this.cashFlowUseCase.getUserForFilter(
          params,
          filterType,
        );
        const allUsers = (res ?? []).flatMap((item) => item?.users ?? []);
        const mappedUsers = allUsers.map((user) => ({
          id: user.id,
          name: user.fullName || user.email || user.name || user.phone || "",
          code: user.code,
        }));
        guard?.runIfLatest(() =>
          this.setObservedState({
            userOptions: this.toUserOptions(mappedUsers),
          }),
        );
      } catch (error) {
        guard?.runIfLatest(() => {
          this.setObservedState({ userOptions: [] });
          this.output.onError.next(error as Error);
        });
      }
    });
  }
}
